import { useState } from "react";
import { Loader2, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export default function WaitlistDialog({ addon, onClose }) {
  const [form, setForm] = useState({ email: "", name: "", organization: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // {status: 'joined' | 'already_on_waitlist'}

  if (!addon) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/waitlist", { addon_id: addon.id, ...form });
      setDone({ status: res.data.status });
      if (res.data.status === "joined") {
        toast.success(`You're on the ${addon.name} waitlist!`);
      } else {
        toast.info("You're already on this waitlist.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="waitlist-dialog"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl fade-in-up"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C07A60]">
              Coming Soon
            </p>
            <h3 className="mt-1 font-heading font-semibold text-xl text-[#1C211F]">
              {addon.name}
            </h3>
            <p className="mt-1 text-sm text-[#57605A]">
              ${addon.amount}/mo · {addon.description}
            </p>
          </div>
          <button
            data-testid="waitlist-close-btn"
            onClick={onClose}
            className="text-[#57605A] hover:text-[#1C211F]"
            aria-label="Close"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {done ? (
          <div className="mt-6 text-center py-4">
            <div className="w-12 h-12 bg-[#E6F0EB] rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-7 h-7 text-[#2E5C50]" strokeWidth={1.5} />
            </div>
            <h4 className="mt-3 font-heading font-semibold text-lg text-[#1C211F]">
              {done.status === "joined" ? "You are on the list!" : "Already signed up"}
            </h4>
            <p className="mt-2 text-sm text-[#57605A]">
              We will email you the moment {addon.name} is ready. No spam, promise.
            </p>
            <button
              data-testid="waitlist-done-btn"
              onClick={onClose}
              className="mt-5 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-5 py-2 text-sm font-medium"
            >
              Got it
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 space-y-3">
            <input
              data-testid="waitlist-email-input"
              required
              type="email"
              placeholder="Your work email"
              value={form.email}
              onChange={set("email")}
              className="w-full bg-white border border-[#E8E6E1] rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50]"
            />
            <input
              data-testid="waitlist-name-input"
              type="text"
              placeholder="Your name (optional)"
              value={form.name}
              onChange={set("name")}
              className="w-full bg-white border border-[#E8E6E1] rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50]"
            />
            <input
              data-testid="waitlist-org-input"
              type="text"
              placeholder="Agency / organization (optional)"
              value={form.organization}
              onChange={set("organization")}
              className="w-full bg-white border border-[#E8E6E1] rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50]"
            />
            <button
              data-testid="waitlist-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md py-2.5 text-sm font-medium disabled:opacity-60"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Join the waitlist
            </button>
            <p className="text-xs text-[#57605A] text-center pt-1">
              We will only email you about {addon.name}. Unsubscribe anytime.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
